import { Button, Chip, Drawer, Stack, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'

import {
  AddTagsForm,
  type AddTagsFormProps
} from 'components/common/AddTagsForm'
import {
  ChipListMultiValue,
  type ChipListMultiValueProps
} from 'components/common/chips/ChipList'
import Icon from 'components/Icon'
import {
  validateServiceTagName,
  validateServiceTagValues
} from 'components/services/service-tag-utils'
import { formatStatusCheckTag } from 'components/status-checks/status-checks-form-utils'
import { usePopoverState } from 'hooks/use-popover-state'
import toast from 'lib/toast'
import { type MultiSelectTags } from 'types/api/services/service'
import { naturalSortBy, unique } from 'utils/list'
import { omit } from 'utils/obj'

import type { ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'

// Note: remove this function once tags are available, access them directly from statusCheck
function getStatusCheckTags(_statusCheck: ServiceStatusCheckFromStatusCheck) {
  return {}
}

export const EditMonitorDetailsDrawer: React.FC<{
  monitorTags?: string[]
  statusCheck: ServiceStatusCheckFromStatusCheck
}> = ({ monitorTags, statusCheck }) => {
  const drawerState = usePopoverState()

  const [tagsState, setTagsState] = useState<null | MultiSelectTags>(null)
  const [isDirty, setIsDirty] = useState(false)

  const handleAddTag: AddTagsFormProps['onAdd'] = ({ name, values }) => {
    setTagsState((prevTagsState) => {
      return {
        ...prevTagsState,
        [name]: naturalSortBy(values)
      }
    })

    setIsDirty(true)
  }

  const handleDeleteTag: Required<ChipListMultiValueProps>['onDeleteTag'] = ([
    tagName
  ]) => {
    setTagsState((prevTagsState) => {
      return prevTagsState ? omit(prevTagsState, tagName) : null
    })

    setIsDirty(true)
  }

  const { tags, tagNames, tagValues } = useMemo(() => {
    /* on mount, set the existing tags on state
     * they can be added to and can be deleted
     * they'll become the source of truth and the data sent to the backend
     */
    if (!tagsState && getStatusCheckTags(statusCheck)) {
      setTagsState(getStatusCheckTags(statusCheck))
    }

    const newTags = tagsState ?? {}

    return {
      tags: newTags,
      tagNames: Object.keys(newTags),
      tagValues: unique(
        Object.values(newTags).flatMap((tagValues: string[]) => {
          return tagValues.flatMap((tagValue) => {
            return tagValue
          })
        })
      )
    }
  }, [setTagsState, statusCheck, tagsState])

  // Note: this will be an API call when ready
  const updateTags = async () => {
    toast({ text: `submitted: ${JSON.stringify(tags)}` })
    setIsDirty(false)
  }

  return (
    <>
      <Button
        size="small"
        startIcon={<Icon css={{ width: '.9rem' }} icon="pencil" />}
        variant="contained"
        onClick={drawerState.open}
      >
        Edit
      </Button>
      <Drawer
        anchor="right"
        open={drawerState.isOpen}
        PaperProps={{
          sx: {
            padding: 6,
            height: '100%',
            width: 800
          }
        }}
        onClose={drawerState.close}
      >
        <Stack sx={{ rowGap: 3 }}>
          <Typography component="h2" variant="h6">
            Edit Tags
          </Typography>
          <Typography>
            Health Checks tags can be used to annotate identifying information.
            Tags can also be used to help route PNIs. Tags that come from the
            observability tool cannot be deleted.
          </Typography>
          <AddTagsForm
            tagNames={tagNames}
            tagValues={tagValues}
            // Note: validation matches the service tag validation
            validateTagName={validateServiceTagName}
            validateTagValues={validateServiceTagValues}
            onAdd={handleAddTag}
          />
          {/* ChipLists wrapper - status check and monitor tags */}
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              columnGap: 1,
              flexWrap: 'wrap',
              rowGap: 1
            }}
          >
            <ChipListMultiValue
              size="medium"
              tags={tags}
              onDeleteTag={handleDeleteTag}
            />
            {
              /* Note: these are read-only */
              monitorTags?.map((tag) => {
                return (
                  <Chip
                    key={tag}
                    label={formatStatusCheckTag(tag)}
                    role="status"
                  />
                )
              })
            }
          </Stack>
          {/* Drawer actions */}
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              columnGap: 1,
              marginTop: 4
            }}
          >
            <Button
              disabled={!isDirty}
              variant="contained"
              onClick={updateTags}
            >
              Save
            </Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={drawerState.close}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  )
}
